import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Link,
    Grid,
    Snackbar,
    Stack,
    Alert
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { sharedQuotesList, allVersionApi } from 'services';
import moment from 'moment';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { createSearchParams, useNavigate } from 'react-router';

const baseURL = process.env.REACT_APP_BASE_URL;

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));
export interface DialogTitleProps {
    id: string;
    children?: React.ReactNode;
    onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
    const { children, onClose, ...other } = props;
    return (
        <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
}

export const SharedQuotes = ({
    sharedQuoteNumber,
    open,
    onClose,
    isShowAllPopup,
    currentRowItem,
    isSuccessMsg
}: any) => {
    const [sharedInfo, setSharedInfo] = useState([]);
    const [downloadSuccess, setDownloadSuccess] = useState(false);
    const navigate = useNavigate();

    let isUpdate = isSuccessMsg ? "true" : "false";

    useEffect(() => {
        if (sharedQuoteNumber || isUpdate) {
            quoteList();
        }
    }, [sharedQuoteNumber, isSuccessMsg])

    useEffect(() => {
        if (currentRowItem) {
            allVersionQuotes();
        }
    }, [currentRowItem])

    const quoteList = () => {
        if(sharedQuoteNumber?.id){
            sharedQuotesList(sharedQuoteNumber?.id).then((res: any) => {
                if (res.status === 200) {
                    setSharedInfo(res?.data);
                }
            });
        }       
    }

    const allVersionQuotes = () => {
        allVersionApi(currentRowItem?.id).then((res: any) => {
            if (res.status === 200) {
                setSharedInfo(res?.data);
            }
        });
    }

    const dateRender = (props: GridRenderCellParams<Date>) => {
        const { row } = props;
        return (
            <div>
                {!row.deliveredOn ? '' : moment(row.deliveredOn).format("DD/MM/YYYY")}
            </div>
        );
    };

    const downloadSharedQuotes = (item: any) => {
        setDownloadSuccess(true);
        const token = localStorage.getItem('token');
        let params = {
            "fileName": item?.reportName,
            "fileShareName": "quotes",
            "clientId": item?.clientId
        }
        axios.post(`${baseURL}/FileShare/GetReport`, params,
            {
                responseType: 'arraybuffer',
                headers: {
                    'Authorization': token ? `Bearer ${token}` : '',
                    "content-type": "application/json"
                }
            }
        ).then((response) => {            
            const url = URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', item.reportName);
            document.body.appendChild(link);
            link.click();
        });
    }

    const reportNameRender = (props: any) => {
        const { row } = props;
        return (
            <Link
                component="button"
                variant="body2"
                onClick={() => downloadSharedQuotes(row)}
            >
                {row.reportName}
            </Link>
        )
    }

    const sharedColumns: GridColDef[] = [
        {
            field: 'reportName',
            headerName: 'Report Name',
            width: 220,
            renderCell: reportNameRender
        },
        {
            field: 'recipientName',
            headerName: 'Report Name',
            width: 220
        },
        {
            field: 'recipientEmailID',
            headerName: 'Recipient Email Id',
            width: 300
        },
        {
            field: 'deliveredOn',
            headerName: 'Delivered On',
            width: 140,
            renderCell: dateRender
        }
    ];

    const createDateRender = (props: GridRenderCellParams<Date>) => {
        const { row } = props;
        return (
            <div>
                {!row.createdOn ? '' : moment(row.createdOn).format("DD/MM/YYYY")}
            </div>
        );
    };

    const viewQuoteRender = (props: any) => {
        const { row } = props;

        return (
            <Link
                component="button"
                variant="body2"
                onClick={() => {
                    navigate({
                        pathname: '/home/reviewQuote',
                        search: createSearchParams({
                            id: row?.id
                        }).toString()
                    });
                }}
            >
                {row.quoteNumber}
            </Link>
        );
    };

    const showAllColumns: GridColDef[] = [        
        {
            field: 'quoteNumber',
            headerName: 'Quote Number',
            width: 220,
            renderCell: viewQuoteRender
        },
        {
            field: 'clientName',
            headerName: 'Client Name',
            width: 220
        },
        {
            field: 'projectName',
            headerName: 'Project Name',
            width: 220
        },
        {
            field: 'revision',
            headerName: 'Revision',
            width: 100
        },
        {
            field: 'createdByName',
            headerName: 'Created By',
            width: 250
        },
        {
            field: 'createdOn',
            headerName: 'Created On',
            width: 140,
            renderCell: createDateRender
        }
    ];

    return (
        <div id="sharedId">
            <BootstrapDialog
                onClose={onClose}
                aria-labelledby="customized-dialog-title"
                open={open}
                sx={{
                    '.MuiPaper-root': {
                        width: '100%',
                        maxWidth: '80%'
                    }
                }}
            >
                <BootstrapDialogTitle id="customized-dialog-title" onClose={onClose}>
                    {isShowAllPopup ? 'Show All Revisions' : 'Shared Quotes'}
                </BootstrapDialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={12} md={12}>
                            <DataGrid
                                autoHeight
                                disableSelectionOnClick
                                sx={{
                                    '.MuiDataGrid-columnHeaders': {
                                        backgroundColor: '#003883',
                                        color: '#fff'
                                    },
                                    '.MuiDataGrid-menuIcon .MuiSvgIcon-root': {
                                        color: '#fff'
                                    },
                                    '.MuiDataGrid-iconButtonContainer .MuiSvgIcon-root': {
                                        color: '#fff'
                                    }
                                }}
                                columns={isShowAllPopup ? showAllColumns : sharedColumns}
                                rows={sharedInfo}
                                pageSize={10}
                                rowsPerPageOptions={[10]}
                                checkboxSelection={false}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
            </BootstrapDialog>
            <Stack sx={{ width: '100%', marginTop: 2 }} spacing={2}>
                <Snackbar open={downloadSuccess} autoHideDuration={3000} onClose={() => setDownloadSuccess(false)}>
                    <Alert severity="success" onClose={() => setDownloadSuccess(false)}>Downloading the report!</Alert>
                </Snackbar>
            </Stack>
        </div>
    );
}
